/**
 * Application metadata and research provenance.
 *
 * Version strings are separated deliberately: the software version, the
 * verification dataset version, and the research artefact versions can move
 * independently, and a summary document needs to state all of them.
 */

import type { ProvenanceMetadata } from '../types';

export const APP_META = {
  name: 'Accelerated Aging Explorer',
  tagline: 'See the assumptions behind the number.',
  version: 'POC v0.1',
  calculatorVerificationVersion: 'v0.1',
  verificationDatasetVersion: 'dev-cases-2026-08 (development status only)',
  buildScopeNote:
    'Proof of concept. Not a validated calculator, not a regulated software product, and not a substitute for engineering judgement.',
} as const;

export const RESEARCH_PROVENANCE: ProvenanceMetadata[] = [
  {
    label: 'Primary research source',
    value: 'Not supplied to this build',
    note:
      'The specification names a source document — “Human-Centered Accelerated Aging Decision-Support Tool — Proof of Concept: Structured Scoping Review and Evidence Map”. It was not attached to this build and was not found in the connected Drive. Research-derived content is therefore marked as pending rather than filled in.',
  },
  {
    label: 'Research protocol version',
    value: 'Pending',
    note: 'To be recorded from the evidence map once available.',
  },
  {
    label: 'Search cutoff',
    value: 'Pending',
    note: 'To be recorded from the evidence map once available.',
  },
  {
    label: 'Source register version',
    value: 'Pending',
  },
  {
    label: 'POC version',
    value: APP_META.version,
  },
  {
    label: 'Calculator verification version',
    value: APP_META.calculatorVerificationVersion,
  },
  {
    label: 'Verification dataset version',
    value: APP_META.verificationDatasetVersion,
    note: 'No independently verified reference cases are present.',
  },
  {
    label: 'Bibliographic details in the reference panel',
    value: 'Public catalogue records, retrieved during the build',
    note: 'Catalogue records identify documents. They are not the documents, and no licensed text was reviewed.',
  },
];

export const RESEARCH_BASIS_STATEMENT =
  'This proof of concept was specified on the basis of an AI-assisted structured scoping review and evidence map. That is not a PRISMA-compliant systematic review, and it should not be described as one. The methodology may later be archived in a research repository, which is why version fields are recorded explicitly above.';

export const BUILD_GAP_STATEMENT =
  'The evidence map itself was not available when this application was built. Everything the specification attributed to that research is marked “specification-derived” or “pending” in the interface, so that nothing reads as a research finding when it is not. Re-tier that content against the evidence map before this POC is shown to anyone as research output.';

export const DISCLAIMER_SHORT =
  'For engineering education and calculation support only. This tool calculates and exposes assumptions. It does not determine whether any temperature, Q10 value, humidity condition, shelf-life claim or test protocol is appropriate for your product or packaging system.';

export const DISCLAIMER_FULL = [
  'For engineering education and calculation support only.',
  'This tool provides accelerated-aging calculations, sensitivity analysis, and contextual technical considerations to improve visibility into assumptions underlying the calculation. It does not determine whether a selected aging temperature, Q10 value, humidity condition, shelf-life claim, or test protocol is appropriate for a specific product or packaging system.',
  'Users remain responsible for applying current applicable standards, product- and material-specific knowledge, risk assessment, validation requirements, organizational procedures, regulatory requirements, and qualified engineering judgment.',
  'Verify technical and normative requirements against the current licensed editions of applicable standards before use in engineering or validation activities.',
];
